import { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { X } from "lucide-react";
import { assignManager, changeManager, getEmployeeManager, removeManager } from "../hierarchyApi";
import { descendantIds } from "../hierarchy/hierarchyTree";
import ManagerEmployeeSelect from "./ManagerEmployeeSelect";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { normalizeApiError } from "../../../utils/normalizeApiError";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    borderColor: state.isFocused ? "#0047d6" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(0,71,214,0.12)" : "none",
    "&:hover": { borderColor: "#94a3b8" },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const toOption = (node) => ({
  value: String(node.employee_id),
  label: `${node.employee_name}${node.employee_code ? ` (${node.employee_code})` : ""}`,
  node,
});

/**
 * One modal for both "Assign" and "Change" reporting manager.
 * Saves through the existing /employees/{id}/manager endpoints; the backend
 * also rejects self-reporting and circular chains.
 */
export default function ReportingManagerModal({
  open,
  onClose,
  onSaved,
  index,
  topAdmin,
  initialEmployee = null,
  lockEmployee = false,
}) {
  const [employee, setEmployee] = useState(null);
  const [manager, setManager] = useState(null);
  const [toAdmin, setToAdmin] = useState(false);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmployee(initialEmployee ? toOption(initialEmployee) : null);
    setManager(null);
    setToAdmin(false);
    setError("");
  }, [open, initialEmployee]);

  const employeeId = employee?.value || null;

  // Current manager decides assign (POST) vs change (PUT).
  useEffect(() => {
    let cancelled = false;
    setCurrent(null);
    if (!open || !employeeId) return undefined;
    getEmployeeManager(employeeId)
      .then((m) => !cancelled && setCurrent(m?.employee_id ? m : null))
      .catch(() => !cancelled && setCurrent(null));
    return () => {
      cancelled = true;
    };
  }, [open, employeeId]);

  const employeeOptions = useMemo(
    () => (index?.list || []).map(toOption).sort((a, b) => a.label.localeCompare(b.label)),
    [index]
  );

  // The employee and everyone under them can't become their manager.
  const blockedIds = useMemo(
    () => (employeeId && index ? descendantIds(index, employeeId) : new Set()),
    [index, employeeId]
  );

  // Effective roles from the hierarchy API (linked login account wins over employees.role).
  const resolveRole = useCallback((id) => index?.byId.get(String(id))?.node?.role || null, [index]);

  const adminId = topAdmin ? String(topAdmin.employee_id) : null;
  const canReportToAdmin = Boolean(adminId && employeeId && adminId !== employeeId && !blockedIds.has(adminId));
  const alreadyUnderAdmin = Boolean(adminId && current && String(current.employee_id) === adminId);

  if (!open) return null;

  const targetManagerId = toAdmin ? adminId : manager?.value ? String(manager.value) : null;

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleSave = async () => {
    if (!employeeId || !targetManagerId) return;
    if (targetManagerId === employeeId) {
      setError("An employee cannot report to themselves.");
      return;
    }
    if (blockedIds.has(targetManagerId)) {
      setError("This would create a circular reporting line — the selected manager reports to this employee.");
      return;
    }
    if (current && String(current.employee_id) === targetManagerId) {
      setError("This employee already reports to the selected manager.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = { manager_id: targetManagerId };
      if (current) await changeManager(employeeId, payload);
      else await assignManager(employeeId, payload);
      onSaved?.(employeeId);
      onClose();
    } catch (err) {
      setError(normalizeApiError(err) || "Failed to save reporting manager");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    setError("");
    try {
      await removeManager(employeeId);
      setConfirmRemove(false);
      onSaved?.(employeeId);
      onClose();
    } catch (err) {
      setConfirmRemove(false);
      setError(normalizeApiError(err) || "Failed to remove reporting manager");
    } finally {
      setSaving(false);
    }
  };

  const title = lockEmployee && current ? "Change Reporting Manager" : "Assign Reporting Manager";

  return (
    <div className="ph2-modal-backdrop" onClick={handleClose} role="presentation">
      <div
        className="ph2-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ph2-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ph2-modal-head">
          <h2 id="ph2-modal-title">{title}</h2>
          <button type="button" className="ph2-icon-btn" onClick={handleClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="ph2-modal-body">
          <div className="ph2-field">
            <label htmlFor="ph2-emp-select">
              Employee <span className="ph2-req">*</span>
            </label>
            <Select
              inputId="ph2-emp-select"
              options={employeeOptions}
              value={employee}
              onChange={(opt) => {
                setEmployee(opt);
                setManager(null);
                setToAdmin(false);
                setError("");
              }}
              isDisabled={lockEmployee}
              isClearable={!lockEmployee}
              placeholder="Search employee by name or code…"
              styles={selectStyles}
              menuPortalTarget={document.body}
              noOptionsMessage={() => "No matching employee"}
            />
            {employeeId ? (
              <p className="ph2-field-hint">
                Current manager:{" "}
                <strong>{current ? current.employee_name : "None"}</strong>
              </p>
            ) : null}
          </div>

          <div className="ph2-field">
            <label>
              Reporting Manager <span className="ph2-req">*</span>
            </label>
            <ManagerEmployeeSelect
              value={toAdmin && topAdmin ? toOption(topAdmin) : manager}
              onChange={setManager}
              excludeEmployeeId={employeeId}
              excludeEmployeeIds={blockedIds}
              resolveRole={resolveRole}
              disabled={!employeeId || toAdmin}
              placeholder="Search and select manager…"
              compact
            />
          </div>

          {canReportToAdmin && !alreadyUnderAdmin ? (
            <label className="ph2-check">
              <input
                type="checkbox"
                checked={toAdmin}
                onChange={(e) => {
                  setToAdmin(e.target.checked);
                  setError("");
                }}
              />
              Report directly to Admin ({topAdmin.employee_name})
            </label>
          ) : null}

          {error ? <p className="ph2-error" role="alert">{error}</p> : null}
        </div>

        <div className="ph2-modal-foot">
          {current ? (
            <button
              type="button"
              className="ph2-link-danger"
              onClick={() => setConfirmRemove(true)}
              disabled={saving}
            >
              Remove current manager
            </button>
          ) : (
            <span />
          )}
          <div className="ph2-modal-actions">
            <button type="button" className="ph2-btn ph2-btn--secondary" onClick={handleClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              className="ph2-btn ph2-btn--primary"
              onClick={handleSave}
              disabled={!employeeId || !targetManagerId || saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {/* Inside the dialog box so its clicks don't reach the backdrop's close handler. */}
        <ConfirmDialog
          open={confirmRemove}
          title="Remove reporting manager?"
          message={`${employee?.node?.employee_name || "This employee"} will no longer report to anyone. Their own direct reports are not affected.`}
          confirmLabel="Remove manager"
          onConfirm={handleRemove}
          onCancel={() => setConfirmRemove(false)}
          loading={saving}
          danger
        />
      </div>
    </div>
  );
}
